import { Activity as ActivityIcon, Network, ChevronRight, X } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { id as localeId } from 'date-fns/locale';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { StepStatus, ACTIVITY_ACTION_LABEL } from './dashboard.types';
import { WelcomeBanner } from './WelcomeBanner';
import { ImplementationTimelineCard } from './ImplementationTimelineCard';
import { useStepProgress, useActivities } from '@/api/dashboard';
import useTenantState from '@/states/tenant.state';
import useThemeState from '@/states/theme.state';
import {
  useGetChecklistInstanceByCompany,
  useImplementationTimeline,
} from '@/pages/company-profile/checklist/useChecklist';
import { useGetCompanyData } from '@/pages/company-profile/company-data/useCompanyData';
import { normalizeSteps, deriveStepStatuses } from '@/utils/functions/stepProgress';

// Primary brand dark blue used across the "Perancangan AOS" area.
const BRAND_DARK = '#071753';
const IN_PROGRESS_ORANGE = '#FD6118';

const STEP_KEY_TO_ROUTE: Record<string, string> = {
  purpose: 'purpose',
  positioning: 'positioning',
  vision: 'vision',
  mission: 'mission',
  culture: 'culture',
  business_goal: 'business-goal',
  organization_structure: 'organization-structure',
  key_activities: 'key-activities',
  job_description: 'job-description',
  report: 'report',
  kpi: 'kpi',
  reward: 'reward-punishment',
  punishment: 'reward-punishment',
  sop: 'sop',
  train_the_trainer: 'train',
};

// Circle number shown in the timeline — mirrors the sidebar's stepNumber values.
// Both purpose and positioning show "0"; remaining steps are 1–12.
const STEP_DISPLAY_NUMBER: Record<string, number> = {
  purpose: 0,
  positioning: 0,
  vision: 1,
  mission: 2,
  culture: 3,
  business_goal: 4,
  organization_structure: 5,
  key_activities: 6,
  job_description: 7,
  report: 8,
  kpi: 9,
  reward: 10,
  sop: 11,
  train_the_trainer: 12,
};

const Legend = () => {
  const isDark = useThemeState(s => s.mode === 'dark');
  const doneBg = isDark ? IN_PROGRESS_ORANGE : BRAND_DARK;

  return (
    <div className="flex flex-wrap items-center gap-4 text-xs">
      <span className="flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: doneBg }} />
        <span className="text-gray-600 dark:text-gray-300">Selesai</span>
      </span>
      <span className="flex items-center gap-1.5">
        <span
          className="h-2.5 w-2.5 rounded-full border-2 bg-white dark:bg-gray-800"
          style={{ borderColor: IN_PROGRESS_ORANGE }}
        />
        <span className="text-gray-600 dark:text-gray-300">Sedang Dikerjakan</span>
      </span>
      <span className="flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-full border-2 border-gray-300 bg-white dark:border-white dark:bg-gray-800" />
        <span className="text-gray-600 dark:text-gray-300">Belum Dimulai</span>
      </span>
    </div>
  );
};

const StepCircle = ({ status, number }: { status: StepStatus; number: number }) => {
  const isDark = useThemeState(s => s.mode === 'dark');
  const doneBg = isDark ? IN_PROGRESS_ORANGE : BRAND_DARK;

  if (status === 'done') {
    return (
      <div className="relative inline-flex shrink-0">
        <div
          className="flex h-11 w-11 items-center justify-center rounded-full text-base font-bold text-white"
          style={{ backgroundColor: doneBg }}
        >
          {number}
        </div>
        <span
          className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full text-white ring-2 ring-white dark:ring-gray-800"
          style={{ backgroundColor: doneBg, fontSize: '9px' }}
        >
          ✓
        </span>
      </div>
    );
  }

  if (status === 'in_progress') {
    return (
      <div
        className="flex h-11 w-11 items-center justify-center rounded-full border-[3px] bg-white text-base font-bold dark:bg-gray-800"
        style={{ borderColor: IN_PROGRESS_ORANGE, color: IN_PROGRESS_ORANGE }}
      >
        {number}
      </div>
    );
  }

  return (
    <div className="flex h-11 w-11 items-center justify-center rounded-full border-[3px] border-gray-300 bg-white text-base font-bold text-gray-400 dark:border-white dark:bg-gray-800 dark:text-white">
      {number}
    </div>
  );
};

const stepTextColor = (status: StepStatus) => {
  if (status === 'done') return 'font-semibold text-gray-900 dark:text-[#FD6118]';
  if (status === 'in_progress') return 'text-[#FD6118] font-semibold';
  return 'text-gray-400 dark:text-white';
};

const Card = ({ children }: { children: React.ReactNode }) => (
  <div className="w-full rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900 md:p-6">
    {children}
  </div>
);

const StepTimelineSkeleton = () => (
  <div className="overflow-x-auto pb-2">
    <div className="flex min-w-max animate-pulse items-start gap-2">
      {Array.from({ length: 14 }).map((_, i) => (
        <div key={i} className="flex items-start">
          <div className="flex w-24 flex-col items-center">
            <div className="h-11 w-11 rounded-full bg-gray-200 dark:bg-gray-700" />
            <div className="mt-2 h-3 w-16 rounded bg-gray-200 dark:bg-gray-700" />
          </div>
          {i < 13 && <div className="mt-[22px] h-[3px] w-6 shrink-0 rounded-full bg-gray-200 dark:bg-gray-700" />}
        </div>
      ))}
    </div>
  </div>
);

const ActivitySkeleton = () => (
  <ul className="animate-pulse divide-y divide-gray-100 dark:divide-gray-800">
    {Array.from({ length: 3 }).map((_, i) => (
      <li key={i} className="flex items-center gap-3 py-3">
        <div className="h-8 w-8 shrink-0 rounded-full bg-gray-200 dark:bg-gray-700" />
        <div className="flex-1 space-y-1.5">
          <div className="h-3 w-3/4 rounded bg-gray-200 dark:bg-gray-700" />
          <div className="h-2.5 w-1/3 rounded bg-gray-200 dark:bg-gray-700" />
        </div>
        <div className="h-2.5 w-16 shrink-0 rounded bg-gray-200 dark:bg-gray-700" />
      </li>
    ))}
  </ul>
);

const ActivityHistoryModal = ({ onClose }: { onClose: () => void }) => {
  const allActivitiesQuery = useActivities(200);
  const allActivities = allActivitiesQuery.data ?? [];
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={e => {
        if (e.target === overlayRef.current) onClose();
      }}
    >
      <div className="flex max-h-[80vh] w-full max-w-lg flex-col rounded-2xl bg-white shadow-xl dark:bg-gray-900">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">Semua Aktivitas</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300"
          >
            <X size={18} />
          </button>
        </div>

        <div className="overflow-y-auto px-5">
          {allActivitiesQuery.isLoading && <ActivitySkeleton />}
          {allActivitiesQuery.isError && <QueryError onRetry={() => allActivitiesQuery.refetch()} />}
          {!allActivitiesQuery.isLoading &&
            !allActivitiesQuery.isError &&
            (allActivities.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">Belum ada aktivitas.</p>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                {allActivities.map(activity => (
                  <li key={activity.id} className="flex items-center justify-between gap-3 py-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-50 dark:bg-gray-800">
                        <ActivityIcon size={16} style={{ color: BRAND_DARK }} />
                      </div>
                      <p className="text-sm text-gray-700 dark:text-gray-200">
                        <span className="font-semibold">{activity.step_name}</span>{' '}
                        {ACTIVITY_ACTION_LABEL[activity.action]}
                      </p>
                    </div>
                    <span className="shrink-0 text-xs text-gray-400 dark:text-gray-500">
                      {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true, locale: localeId })}
                    </span>
                  </li>
                ))}
              </ul>
            ))}
        </div>
      </div>
    </div>
  );
};

const QueryError = ({ onRetry }: { onRetry: () => void }) => (
  <div className="flex flex-col items-center gap-3 py-6 text-center">
    <p className="text-sm text-gray-500 dark:text-gray-400">Gagal memuat data.</p>
    <button
      type="button"
      onClick={onRetry}
      className="rounded-lg border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
    >
      Coba lagi
    </button>
  </div>
);

const OnboardingModal = ({ onStart }: { onClose?: () => void; onStart: () => void }) => {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="flex w-full max-w-md flex-col items-center rounded-2xl bg-white p-6 text-center shadow-xl dark:bg-gray-900">
        <h2 className="mb-2 text-xl font-bold text-gray-900 dark:text-white">Selamat Datang!</h2>
        <p className="mb-6 text-sm text-gray-600 dark:text-gray-300">
          Sebagai langkah pertama, silakan lengkapi Profil Perusahaan Anda sebelum melanjutkan proses perancangan AOS.
        </p>
        <button
          onClick={onStart}
          className="w-full rounded-lg px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:opacity-90"
          style={{ backgroundColor: BRAND_DARK }}
        >
          Mulai Isi Data Perusahaan
        </button>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { tenant_id } = useParams();
  const { currentTenant } = useTenantState();
  const isDark = useThemeState(s => s.mode === 'dark');
  const [showAllActivities, setShowAllActivities] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const stepProgressQuery = useStepProgress();
  const activitiesQuery = useActivities(10);

  const { data: companyData, isLoading: isCompanyDataLoading } = useGetCompanyData(currentTenant?.id);
  const companyDataId = companyData?.id;

  useEffect(() => {
    if (!isCompanyDataLoading && !companyDataId) {
      const timer = setTimeout(() => {
        setShowOnboarding(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isCompanyDataLoading, companyDataId]);
  const instanceQuery = useGetChecklistInstanceByCompany(currentTenant?.id, companyDataId);
  const instanceId = instanceQuery.data?.id;
  const timelineQuery = useImplementationTimeline(currentTenant?.id, instanceId, Boolean(instanceId));

  const timeline =
    timelineQuery.data && instanceQuery.data && timelineQuery.data.days
      ? (() => {
          const responses = instanceQuery.data.responses || [];
          const completedCount = responses.filter(r => r.status === 'done' || r.is_completed).length;
          const totalCount = responses.length;
          const clientProgressPct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

          // Build map of item_id to estimated_work_days from template sections
          const itemDaysMap = new Map<number, number>();
          instanceQuery.data.template?.sections?.forEach(section => {
            section.sub_sections?.forEach(subSection => {
              subSection.items?.forEach(item => {
                if (item.estimated_work_days != null) {
                  itemDaysMap.set(item.id, item.estimated_work_days);
                }
              });
            });
          });

          const currentDay = timelineQuery.data.current_day;
          const totalDays = timelineQuery.data.total_days;

          // Find the first day that is not fully completed
          let activeDay = currentDay;
          for (let d = 1; d <= totalDays; d++) {
            const responsesForDay = responses.filter(r => {
              const estimatedDay = itemDaysMap.get(r.item_id) ?? r.item?.estimated_work_days;
              return estimatedDay === d;
            });
            if (responsesForDay.length > 0) {
              const allCompleted = responsesForDay.every(r => r.status === 'done' || r.is_completed);
              if (!allCompleted) {
                activeDay = d;
                break;
              }
            }
          }

          const updatedDays = timelineQuery.data.days.map(dayEntry => {
            const responsesForDay = responses.filter(r => {
              const estimatedDay = itemDaysMap.get(r.item_id) ?? r.item?.estimated_work_days;
              return estimatedDay === dayEntry.day;
            });
            if (responsesForDay.length === 0) return dayEntry;

            const allCompleted = responsesForDay.every(r => r.status === 'done' || r.is_completed);
            if (allCompleted) {
              return { ...dayEntry, status: 'done' as const };
            } else {
              let newStatus: 'running' | 'overdue' | 'upcoming' = 'upcoming';
              if (dayEntry.day === activeDay) {
                newStatus = 'running';
              } else if (dayEntry.day < activeDay) {
                newStatus = 'overdue';
              }
              return { ...dayEntry, status: newStatus };
            }
          });

          return {
            ...timelineQuery.data,
            days: updatedDays,
            progress: {
              ...timelineQuery.data.progress,
              completed: completedCount,
              percentage: clientProgressPct,
            },
          };
        })()
      : timelineQuery.data;

  const showTimeline = Boolean(timeline && timeline.started_at && timeline.total_days > 0);

  const normalizedRaw = stepProgressQuery.data ? normalizeSteps(stepProgressQuery.data.steps) : [];
  const steps = deriveStepStatuses(normalizedRaw);
  // "12 langkah" only counts steps 1-12 (Vision..Train The Trainer); Purpose
  // and Positioning are a preliminary "step 0" and are excluded from this count.
  const countedSteps = steps.filter(s => s.step_key !== 'purpose' && s.step_key !== 'positioning');
  const stepProgress = stepProgressQuery.data
    ? {
        ...stepProgressQuery.data,
        steps,
        total_steps: countedSteps.length,
        completed_steps: countedSteps.filter(s => s.status === 'done').length,
        percentage:
          countedSteps.length > 0
            ? Math.round((countedSteps.filter(s => s.status === 'done').length / countedSteps.length) * 100)
            : 0,
      }
    : undefined;
  const activities = activitiesQuery.data ?? [];

  const inProgressIndex = steps.findIndex(step => step.status === 'in_progress');
  let activeIndex = inProgressIndex;
  let isActuallyInProgress = true;

  if (activeIndex === -1) {
    activeIndex = steps.findIndex(step => step.status === 'not_started');
    isActuallyInProgress = false;
  }
  const activeStep = activeIndex >= 0 ? steps[activeIndex] : null;

  return (
    <>
      <section className="w-full space-y-6 p-5">
        <WelcomeBanner />

        {/* Section 1 — Perancangan AOS */}
        <Card>
          <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                Perancangan AOS{stepProgress ? ` (12 Langkah)` : ''}
              </h2>
              {stepProgress && (
                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500 2xl:hidden">
                  Geser untuk melihat langkah lain &rarr;
                </p>
              )}
            </div>
            <Legend />
          </div>

          {stepProgressQuery.isLoading && (
            <>
              <StepTimelineSkeleton />
              <div className="mt-6 animate-pulse">
                <div className="mb-2 flex items-center justify-between">
                  <div className="h-3 w-40 rounded bg-gray-200 dark:bg-gray-700" />
                  <div className="h-3 w-20 rounded bg-gray-200 dark:bg-gray-700" />
                </div>
                <div className="h-2.5 w-full rounded-full bg-gray-200 dark:bg-gray-700" />
              </div>
            </>
          )}

          {stepProgressQuery.isError && <QueryError onRetry={() => stepProgressQuery.refetch()} />}

          {stepProgress && (
            <>
              {/* In-progress highlight banner */}
              {activeStep && (
                <div
                  className={clsx(
                    'mb-6 flex flex-col gap-3 rounded-xl border p-4 md:flex-row md:items-center md:justify-between',
                    isActuallyInProgress
                      ? 'border-orange-200 bg-orange-50 dark:border-orange-900/50 dark:bg-orange-950/30'
                      : 'border-blue-200 bg-blue-50 dark:border-blue-900/50 dark:bg-blue-950/30',
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-white"
                      style={{ backgroundColor: isActuallyInProgress ? IN_PROGRESS_ORANGE : BRAND_DARK }}
                    >
                      <Network size={20} />
                    </div>
                    <div>
                      <p
                        className="text-xs font-bold uppercase tracking-wide"
                        style={{ color: isActuallyInProgress ? IN_PROGRESS_ORANGE : BRAND_DARK }}
                      >
                        {isActuallyInProgress ? 'Sedang Dikerjakan' : 'Belum Dimulai'} - Langkah{' '}
                        {String(activeIndex + 1).padStart(2, '0')}
                      </p>
                      <p className="text-base font-semibold text-gray-900 dark:text-white">{activeStep.step_name}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (!tenant_id) return;
                      const route = STEP_KEY_TO_ROUTE[activeStep.step_key];
                      if (route) navigate(`/${tenant_id}/${route}`);
                    }}
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:opacity-90"
                    style={{ backgroundColor: BRAND_DARK }}
                  >
                    {isActuallyInProgress ? 'Lanjutkan' : 'Mulai Sekarang'}
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}

              {/* Step circle timeline */}
              <div className="overflow-x-auto pb-2 pt-4">
                <div className="flex min-w-max items-start px-2">
                  {stepProgress.steps.map((step, index) => {
                    const isLast = index === stepProgress.steps.length - 1;
                    const nextStatus = !isLast ? stepProgress.steps[index + 1].status : null;
                    const lineActive = step.status === 'done' && nextStatus && nextStatus !== 'not_started';
                    const activeLineColor = isDark ? IN_PROGRESS_ORANGE : BRAND_DARK;
                    const lineColor = lineActive ? activeLineColor : '#E5E7EB';
                    return (
                      <div
                        key={step.step_id}
                        className={clsx(
                          'relative flex flex-col items-center',
                          !isLast ? 'flex-1 min-w-[100px]' : 'w-24 shrink-0',
                        )}
                      >
                        {!isLast && (
                          <div
                            className="absolute left-[50%] top-[22px] h-[3px] w-full -translate-y-1/2"
                            style={{ backgroundColor: lineColor }}
                          />
                        )}
                        <div className="relative z-10 rounded-full bg-white ring-[4px] ring-white dark:bg-gray-900 dark:ring-gray-900">
                          <StepCircle status={step.status} number={STEP_DISPLAY_NUMBER[step.step_key] ?? index} />
                        </div>
                        <span
                          className={clsx(
                            'mt-2 w-24 shrink-0 text-center text-xs leading-tight',
                            stepTextColor(step.status),
                          )}
                        >
                          {step.step_name}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Progress bar */}
              <div className="mt-6">
                <div className="mb-2 flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-300">
                    {stepProgress.completed_steps} dari {stepProgress.total_steps} langkah selesai
                  </span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {stepProgress.percentage}% Selesai
                  </span>
                </div>
                <div className="h-2.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${stepProgress.percentage}%`,
                      backgroundColor: isDark ? IN_PROGRESS_ORANGE : BRAND_DARK,
                    }}
                  />
                </div>
              </div>
            </>
          )}
        </Card>

        {/* Section 2 — Implementasi AOS timeline */}
        {showTimeline && timeline && (
          <Card>
            <ImplementationTimelineCard timeline={timeline} />
          </Card>
        )}

        {/* Section 3 — Aktivitas Terbaru */}
        <Card>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">Aktivitas Terbaru</h2>
            <button
              type="button"
              onClick={() => setShowAllActivities(true)}
              className="text-sm font-medium hover:underline"
              style={{ color: isDark ? 'white' : BRAND_DARK }}
            >
              Lihat semua
            </button>
          </div>

          {activitiesQuery.isLoading && <ActivitySkeleton />}

          {activitiesQuery.isError && <QueryError onRetry={() => activitiesQuery.refetch()} />}

          {!activitiesQuery.isLoading && !activitiesQuery.isError && (
            <>
              {activities.length === 0 ? (
                <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">Belum ada aktivitas.</p>
              ) : (
                <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                  {activities.map(activity => (
                    <li key={activity.id} className="flex items-center justify-between gap-3 py-3">
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-50 dark:bg-gray-800">
                          <ActivityIcon size={16} style={{ color: BRAND_DARK }} />
                        </div>
                        <p className="text-sm text-gray-700 dark:text-gray-200">
                          <span className="font-semibold">{activity.step_name}</span>{' '}
                          {ACTIVITY_ACTION_LABEL[activity.action]}
                        </p>
                      </div>
                      <span className="shrink-0 text-xs text-gray-400 dark:text-gray-500">
                        {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true, locale: localeId })}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </Card>
      </section>

      {showAllActivities && <ActivityHistoryModal onClose={() => setShowAllActivities(false)} />}

      {showOnboarding && (
        <OnboardingModal
          onClose={() => setShowOnboarding(false)}
          onStart={() => {
            setShowOnboarding(false);
            if (tenant_id) navigate(`/${tenant_id}/company-profile`);
          }}
        />
      )}
    </>
  );
};

export default Dashboard;
